import React from 'react';

const PaperUsagePreview = ({ paperUsage, isBooklet, separateCover }) => {
  // Handle cases where paperUsage might be null or have an error
  if (!paperUsage) {
    return null; // Don't render anything if no data
  }

  if (paperUsage.error) {
    return (
      <div className="mb-6 p-3 bg-red-50 text-red-700 text-sm rounded border border-red-200">
        Could not calculate paper usage: {paperUsage.error}
      </div>
    );
  }

  // Determine if we should show "(Inner)" labels
  const showInnerLabel = isBooklet && separateCover;

  // --- NCR Paper Usage Display ---
  if (paperUsage.is_ncr && paperUsage.ncr_parts_usage) {
    return (
      <div className="mb-6">
        <h3 className="font-medium mb-3">NCR Paper Usage</h3>
        <div className="p-4 bg-gray-50 rounded border space-y-3">
          {paperUsage.ncr_parts_usage.map(part => (
            <div key={part.part_number} className="p-2 border rounded bg-white">
              <h4 className="font-semibold text-sm mb-1">Part {part.part_number}: {part.paper_name}</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><strong>Item Size:</strong> {part.item_width_mm}mm x {part.item_height_mm}mm</div>
                <div><strong>Sheet Size:</strong> {part.sheet_width_mm}mm x {part.sheet_height_mm}mm</div>
                <div><strong>Items per Sheet:</strong> {part.items_per_sheet}</div>
                <div><strong>Sheets Needed:</strong> {part.sheets_needed}</div>
                <div><strong>Sets to Produce:</strong> {part.sets_to_produce}</div>
                <div><strong>Waste:</strong> {part.waste_percentage?.toFixed(1) ?? 'N/A'}%</div>
              </div>
            </div>
          ))}
        </div>
        {/* Display warning if calculation might be inaccurate */}
        {paperUsage.warning && (
          <p className="mt-2 text-xs text-yellow-700 bg-yellow-50 p-1 rounded">{paperUsage.warning}</p>
        )}
      </div>
    );
  }
  // --- END NCR Paper Usage Display ---

  return (
    <div className="mb-6">
      <h3 className="font-medium mb-3">Paper Usage</h3>
      <div className="p-4 bg-gray-50 rounded border">
        <div className="grid grid-cols-2 gap-2 text-sm">
          {/* Corrected Total Sheets */}
          <div><strong>Total Sheets:</strong> {paperUsage.total_sheets_combined ?? 'N/A'}</div>

          {/* Display Inner Page Sheets if available and distinct from combined or if cover exists */}
          {(paperUsage.inner_pages?.total_sheets !== undefined && (showInnerLabel || paperUsage.inner_pages?.total_sheets !== paperUsage.total_sheets_combined)) && (
            <div><strong>Sheets {showInnerLabel ? '(Inner)' : ''}:</strong> {paperUsage.inner_pages.total_sheets ?? 'N/A'}</div>
          )}

          {/* Display Inner Page Waste */}
          <div>
            <strong>Waste {showInnerLabel ? '(Inner)' : ''}:</strong> {paperUsage.inner_pages?.waste_percentage?.toFixed(1) ?? 'N/A'}%
          </div>

          {/* Display Inner Page Sheet Size */}
          <div>
            <strong>Sheet Size {showInnerLabel ? '(Inner)' : ''}:</strong> {paperUsage.inner_pages?.sheet_dimensions ?? 'N/A'}
          </div>

          {/* Display Combined Weight */}
          <div>
            <strong>Total Weight:</strong> {paperUsage.total_weight_kg_combined?.toFixed(2) ?? 'N/A'}kg
          </div>

          {/* Display Packs Needed (Inner and Cover if applicable) */}
          {(paperUsage.inner_pages?.packs_needed > 0 || paperUsage.cover_pages?.packs_needed > 0) && (
            <div className="col-span-2">
              <strong>Packs Needed:</strong>
              {/* Inner part: Add prefix only if cover also exists */}
              {paperUsage.inner_pages?.packs_needed > 0 && (
                (paperUsage.cover_pages?.packs_needed > 0 ? ' Inner: ' : ' ') + paperUsage.inner_pages.packs_needed
              )}
              {/* Cover part: Always add prefix if it exists */}
              {paperUsage.cover_pages?.packs_needed > 0 && (
                ' Cover: ' + paperUsage.cover_pages.packs_needed
              )}
            </div>
          )}

          {/* Display Cover Page details if they exist */}
          {paperUsage.cover_pages && !paperUsage.cover_pages.error && (
            <>
              <div className="col-span-2 mt-2 pt-2 border-t">
                <strong className="text-xs uppercase text-gray-500">Cover Details</strong>
              </div>
              <div><strong>Cover Sheets:</strong> {paperUsage.cover_pages.total_sheets ?? 'N/A'}</div>
              <div><strong>Cover Waste:</strong> {paperUsage.cover_pages.waste_percentage?.toFixed(1) ?? 'N/A'}%</div>
              <div><strong>Cover Sheet Size:</strong> {paperUsage.cover_pages.sheet_dimensions ?? 'N/A'}</div>
            </>
          )}
        </div>
        {/* Display warning if calculation might be inaccurate */}
        {paperUsage.warning && (
          <p className="mt-2 text-xs text-yellow-700 bg-yellow-50 p-1 rounded">{paperUsage.warning}</p>
        )}
      </div>
    </div>
  );
};

export default PaperUsagePreview;